import React, { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Tour from "src/components/Tour";
import {
  getTourCurrentStep,
  getTourDialogReady,
  getTourOpen,
  getTourPendingStep,
  getTourStepType,
} from "src/store/tour/selector";
import {
  closeGlobalTour,
  setCurrentStep,
  setDialogReady,
  setPendingStep,
} from "src/store/tour/actions";
import { TOUR_STEPS } from "src/utils/tour_steps";

const GlobalTour = () => {
  const dispatch = useDispatch();
  const stepType = useSelector(getTourStepType);
  const currentStep = useSelector(getTourCurrentStep);
  const tourOpen = useSelector(getTourOpen);
  const pendingStep = useSelector(getTourPendingStep);
  const dialogReady = useSelector(getTourDialogReady);
  const blurredElsRef = useRef<HTMLElement[]>([]);

  const stepData = stepType ? TOUR_STEPS[stepType] : undefined;

  useEffect(() => {
    if (dialogReady && pendingStep !== null) {
      dispatch(setCurrentStep(pendingStep));
      dispatch(setPendingStep(null));
      dispatch(setDialogReady(false));
    }
  }, [dialogReady, pendingStep, dispatch]);

  const clearBlur = useCallback(() => {
    for (const el of blurredElsRef.current) {
      el.style.filter = "";
      el.style.transition = "";
    }
    blurredElsRef.current = [];
  }, []);

  useEffect(() => {
    if (!tourOpen || !stepData?.[currentStep]?.selector) {
      clearBlur();
      return;
    }

    let cancelled = false;
    let rafId: number;
    const step = stepData[currentStep];
    const selectors = step.highlightedSelectors ?? [step.selector!];

    clearBlur();
    let helperTimeout: ReturnType<typeof setTimeout>;
    let helperRef: HTMLElement | null = null;

    const hideHelper = () => {
      const h = document.querySelector(
        ".reactour__helper"
      ) as HTMLElement | null;
      if (h) {
        helperRef = h;
        h.style.transition = "none";
        h.style.opacity = "0";
      }
      return h;
    };

    hideHelper();

    const showHelper = () => {
      if (cancelled) return;
      const h =
        helperRef ||
        (document.querySelector(".reactour__helper") as HTMLElement | null);
      if (!h) return;
      helperRef = h;
      h.getBoundingClientRect();
      h.style.transition = "opacity 0.4s ease-out";
      h.style.opacity = "1";
    };

    const applyBlur = () => {
      if (cancelled) return;
      const elements = selectors
        .map((s) => document.querySelector(s) as HTMLElement | null)
        .filter((el): el is HTMLElement => el !== null);
      if (elements.length === 0) {
        rafId = requestAnimationFrame(applyBlur);
        return;
      }

      if (!helperRef) hideHelper();

      // Keep tour focus reliable by centering the primary target before
      // applying highlight/helper transitions.
      elements[0].scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "auto",
      });

      blurredElsRef.current = elements;
      for (const el of elements) {
        el.style.transition = "none";
        el.style.filter = "blur(7px)";
        el.getBoundingClientRect();
        el.style.transition = "filter 1.0s ease-out";
        el.style.filter = "blur(0px)";
      }

      helperTimeout = setTimeout(showHelper, 700);
    };

    rafId = requestAnimationFrame(applyBlur);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      clearTimeout(helperTimeout);
      if (helperRef) {
        helperRef.style.transition = "";
        helperRef.style.opacity = "";
      }
    };
  }, [tourOpen, currentStep, stepData, clearBlur]);

  return (
    <Tour
      startAt={0}
      closeWithMask={true}
      disableFocusLock
      steps={stepData || []}
      isOpen={tourOpen}
      onRequestClose={() => dispatch(closeGlobalTour())}
      showButtons={false}
      showCloseButton={false}
      showNavigation={false}
      showNavigationNumber={false}
      showNumber={false}
      disableDotsNavigation
      rounded={12}
      disableInteraction
      goToStep={currentStep}
      updateDelay={250}
    />
  );
};

export default GlobalTour;
